import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import {
  Users,
  Crown,
  ShieldCheck,
  BadgeCheck,
  TrendingUp,
  Activity,
  ChevronRight,
} from "lucide-react";
import {
  collection,
  getCountFromServer,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";
import AdminLayout from "../layout/AdminLayout";
import { PROFILE_VERIFICATION_STATUSES } from "../../utils/verification";

const ONLINE_WINDOW_MS = 2 * 60 * 1000;

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

function toDateLike(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatRelativeTime(value) {
  const date = toDateLike(value);
  if (!date) return "Unknown";

  const diff = Date.now() - date.getTime();
  if (diff < 10 * 1000) return "just now";
  if (diff < 60 * 1000) return `${Math.max(1, Math.floor(diff / 1000))}s ago`;
  if (diff < 60 * 60 * 1000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 24 * 60 * 60 * 1000) return `${Math.floor(diff / 3600000)}h ago`;
  return date.toLocaleDateString();
}

function getNavigationLabel(value) {
  const navigation = String(value || "").toLowerCase();
  if (!navigation) return "In-app navigation";
  if (navigation === "browser-back-forward") return "Browser back/forward";
  if (navigation === "pop") return "Browser back/forward";
  if (navigation === "push") return "New page";
  if (navigation === "replace") return "Page replace";
  return value;
}

const Dashboard = () => {
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["adminDashboardData"],
    queryFn: async () => {
      const profilesRef = collection(db, "profiles");

      const safeCount = async (q) => {
        try {
          const snap = await getCountFromServer(q);
          return snap.data().count;
        } catch {
          return 0;
        }
      };

      const [total, premium, pending, verified, recentSnap, activeSnap] = await Promise.all([
        safeCount(profilesRef),
        safeCount(query(profilesRef, where("membership", "==", "premium"))),
        safeCount(query(profilesRef, where("profileStatus", "==", PROFILE_VERIFICATION_STATUSES.PENDING))),
        safeCount(query(profilesRef, where("profileStatus", "==", PROFILE_VERIFICATION_STATUSES.VERIFIED))),
        getDocs(query(profilesRef, orderBy("createdAt", "desc"), limit(8))),
        getDocs(query(profilesRef, orderBy("lastActiveAt", "desc"), limit(8))).catch(() => null),
      ]);

      return {
        counts: { total, premium, pending, verified },
        recentUsers: recentSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
        activeUsers: activeSnap?.docs.map((doc) => ({ id: doc.id, ...doc.data() })) || [],
      };
    },
    staleTime: 300000,
    cacheTime: 1800000,
    retry: 1,
  });

  const counts = data?.counts || { total: 0, premium: 0, pending: 0, verified: 0 };
  const recentUsers = useMemo(() => data?.recentUsers || [], [data?.recentUsers]);
  const activeUsers = useMemo(() => data?.activeUsers || [], [data?.activeUsers]);
  const loading = isLoading;
  const onlineUsers = useMemo(
    () =>
      activeUsers.filter((u) => {
        const activeAt = toDateLike(u.lastActiveAt);
        return activeAt ? Date.now() - activeAt.getTime() <= ONLINE_WINDOW_MS : false;
      }),
    [activeUsers],
  );

  const stats = [
    {
      title: "Total Users",
      value: counts.total,
      Icon: Users,
      tint: "from-sky-100 to-sky-50 text-sky-700",
      delta: "All registered",
    },
    {
      title: "Premium Members",
      value: counts.premium,
      Icon: Crown,
      tint: "from-amber-100 to-amber-50 text-amber-700",
      delta: counts.total
        ? `${Math.round((counts.premium / counts.total) * 100)}% of users`
        : "—",
    },
    {
      title: "Pending Verification",
      value: counts.pending,
      Icon: ShieldCheck,
      tint: "from-rose-100 to-rose-50 text-rose-600",
      delta: counts.pending > 0 ? "Action required" : "All clear",
    },
    {
      title: "Verified Profiles",
      value: counts.verified,
      Icon: BadgeCheck,
      tint: "from-emerald-100 to-emerald-50 text-emerald-700",
      delta: "Approved",
    },
  ];

  return (
    <AdminLayout>
      {/* HEADER */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="section-subtle">Overview</p>
          <h1 className="mt-1 font-display text-3xl font-bold text-ink-800">Dashboard</h1>
          <p className="mt-1 text-[13.5px] text-ink-400">
            Monitor your matrimony platform health
          </p>
        </div>
        <button
          onClick={() => navigate("/admin/users")}
          className="btn-primary inline-flex w-full justify-center sm:w-auto"
        >
          View all users <ChevronRight size={16} />
        </button>
      </div>

      {/* STATS */}
      <motion.div
        initial="hidden"
        animate="show"
        variants={{ show: { transition: { staggerChildren: 0.06 } } }}
        className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4"
      >
        {stats.map(({ title, value, Icon, tint, delta }) => (
          <motion.div key={title} variants={fadeUp} className="card p-5 transition hover:shadow-elevated">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[12px] font-semibold uppercase tracking-wider text-ink-400">
                  {title}
                </p>
                <h2 className="mt-2 font-display text-3xl font-bold text-ink-800">
                  {loading ? "—" : value.toLocaleString()}
                </h2>
                <p className="mt-1 inline-flex items-center gap-1 text-[12px] font-semibold text-emerald-600">
                  <TrendingUp size={12} /> {delta}
                </p>
              </div>
              <div className={`flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br ${tint}`}>
                <Icon size={22} />
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* MAIN GRID */}
      <div className="mt-6 grid gap-5 xl:grid-cols-3">
        {/* Recent users */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="card p-5 xl:col-span-2"
        >
          <div className="mb-5 flex items-center justify-between">
            <div>
              <h2 className="font-display text-lg font-bold text-ink-800">
                Recent registrations
              </h2>
              <p className="mt-0.5 text-[12px] text-ink-400">Latest profiles on the platform</p>
            </div>
            <button
              onClick={() => navigate("/admin/users")}
              className="text-[13px] font-semibold text-brand-600 hover:underline"
            >
              View all
            </button>
          </div>

          <div className="overflow-x-auto -mx-2">
            <table className="w-full min-w-[700px] border-separate border-spacing-y-1.5">
              <thead>
                <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                  <th className="px-3 py-2">User</th>
                  <th className="px-3 py-2">Gender</th>
                  <th className="px-3 py-2">Membership</th>
                  <th className="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {loading
                  ? Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i}>
                        <td colSpan={4} className="px-3 py-2">
                          <div className="skeleton h-12" />
                        </td>
                      </tr>
                    ))
                  : recentUsers.length === 0
                  ? (
                    <tr>
                      <td colSpan={4} className="px-3 py-10 text-center text-ink-400">No users found</td>
                    </tr>
                  )
                  : recentUsers.map((u) => (
                      <tr
                        key={u.id}
                        onClick={() => navigate(`/admin/users/${u.id}`)}
                        className="cursor-pointer rounded-2xl bg-sand-50 transition hover:bg-white hover:shadow-soft"
                      >
                        <td className="rounded-l-2xl px-3 py-3">
                          <div className="flex items-center gap-3">
                            {u.imageUrls?.[0] ? (
                              <img
                                src={u.imageUrls[0]}
                                alt={u.fullName}
                                className="h-11 w-11 rounded-xl object-cover object-center ring-1 ring-sand-200"
                              />
                            ) : (
                              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50 font-bold text-brand-600">
                                {u.fullName?.charAt(0) || "U"}
                              </div>
                            )}
                            <div className="min-w-0">
                              <h3 className="truncate font-semibold text-ink-800">
                                {u.fullName || "No Name"}
                              </h3>
                              <p className="truncate text-[12px] text-ink-400">
                                {u.mobileNumber || "No Number"}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-[13px] text-ink-700">{u.gender || "-"}</td>
                        <td className="px-3 py-3">
                          {u.membership === "premium" ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-bold text-amber-700">
                              <Crown size={11} /> Premium
                            </span>
                          ) : (
                            <span className="rounded-full bg-sand-100 px-2.5 py-0.5 text-[11px] font-bold text-ink-500">
                              Free
                            </span>
                          )}
                        </td>
                        <td className="rounded-r-2xl px-3 py-3">
                          <span
                            className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${
                              (u.status || "active") === "blocked"
                                ? "bg-rose-50 text-rose-700"
                                : (u.status || "active") === "suspended"
                                ? "bg-amber-50 text-amber-700"
                                : "bg-emerald-50 text-emerald-700"
                            }`}
                          >
                            {u.status || "active"}
                          </span>
                        </td>
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Analytics */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="space-y-5"
        >
          <div className="card p-5">
            <div className="flex items-center gap-2">
              <Activity size={16} className="text-brand-600" />
              <h2 className="font-display text-lg font-bold text-ink-800">Live activity</h2>
            </div>
            <p className="mt-1 text-[12px] text-ink-400">
              {loading ? "Loading live user activity..." : `${onlineUsers.length} users online right now`}
            </p>
            <div className="mt-4 space-y-2">
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="skeleton h-20 rounded-2xl" />
                ))
              ) : onlineUsers.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-sand-200 bg-sand-50 p-4 text-[13px] text-ink-400">
                  No users are actively online yet.
                </div>
              ) : (
                onlineUsers.map((u) => {
                  const isBrowserNav = String(u.lastNavigationType || "").toLowerCase() === "browser-back-forward" || String(u.lastNavigationType || "").toLowerCase() === "pop";

                  return (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => navigate(`/admin/users/${u.id}`)}
                      className="w-full rounded-2xl border border-sand-200 bg-white p-3 text-left transition hover:border-brand-600 hover:shadow-soft"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate font-semibold text-ink-800">{u.fullName || "No Name"}</p>
                          <p className="truncate text-[12px] text-ink-400">
                            {u.lastActivePage || "Unknown page"}
                          </p>
                        </div>
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-bold text-emerald-700">
                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                          Online
                        </span>
                      </div>
                      <div className="mt-2 flex items-center justify-between gap-3 text-[11.5px] text-ink-500">
                        <span>Last active {formatRelativeTime(u.lastActiveAt)}</span>
                        <span className="truncate text-right">
                          {isBrowserNav ? "Browser back/forward" : getNavigationLabel(u.lastNavigationType)}
                        </span>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          <div className="card p-5">
            <div className="flex items-center gap-2">
              <Activity size={16} className="text-brand-600" />
              <h2 className="font-display text-lg font-bold text-ink-800">Distribution</h2>
            </div>
            <div className="mt-5 space-y-4">
              <Bar label="Premium" value={counts.premium} total={counts.total} color="bg-amber-500" />
              <Bar label="Verified" value={counts.verified} total={counts.total} color="bg-emerald-500" />
              <Bar label="Pending" value={counts.pending} total={counts.total} color="bg-brand-600" />
            </div>
          </div>
        </motion.div>
      </div>
    </AdminLayout>
  );
};

const Bar = ({ label, value, total, color }) => {
  const pct = total ? (value / total) * 100 : 0;
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-[12px] font-semibold text-ink-600">
        <span>{label}</span>
        <span className="text-ink-400">{value} · {Math.round(pct)}%</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-sand-200">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className={`h-full rounded-full ${color}`}
        />
      </div>
    </div>
  );
};

export default Dashboard;
